import { useState, useMemo, useEffect, useImperativeHandle, forwardRef } from "react";
import { RiMoneyDollarCircleFill, RiArrowDownSLine, RiArrowUpSLine } from "@remixicon/react";

import Card from "components/Card";
import WarningBox from "components/WarningBox";
import { fmtNumber } from "utils";
import { getMaxSpendingCap } from "utils/planUtils";
import Messages from "misc/Messages";

import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { getErrorFromFetcherData } from "utils/fetcherUtils";
import { PricingOptionsDataRoutePath } from "routes/data-routes/billing-data/pricing-options/route.meta";


const { ratePerThousand } = Messages.billingV2.payg;
const MIN_SPENDING_CAP_USD = 6;

type PaygSubOption = "payg" | "payg-and-sales";

export interface PaygControlsRef {
  submit: () => void;
  reset: () => void;
}

interface PayAsYouGoInlineControlsProps {
  paygEnabled: boolean;
  onStateChange: (hasChanges: boolean, isValid: boolean) => void;
}

const PayAsYouGoInlineControls = forwardRef<PaygControlsRef, PayAsYouGoInlineControlsProps>(
  ({ paygEnabled, onStateChange }, ref) => {
    const { activeSubscription } = useActiveSubscription();
    const { pricingOptions, pricingOptionsFetcher } = usePricingOptions();

    const backendCreditsCap = pricingOptions?.["pay-as-you-go"]?.creditsCap;
    const backendSpendingCap = backendCreditsCap
      ? Math.round((backendCreditsCap * ratePerThousand) / 1000)
      : MIN_SPENDING_CAP_USD;

    const [spendingCap, setSpendingCap] = useState<number>(MIN_SPENDING_CAP_USD);
    const [paygSubOption, setPaygSubOption] = useState<PaygSubOption>("payg");
    const [disclosureOpen, setDisclosureOpen] = useState(false);

    const maxSpendingCap = useMemo(() => getMaxSpendingCap(activeSubscription), [activeSubscription]);

    const creditsCap = useMemo(() => {
      if (spendingCap) {
        return Math.floor((spendingCap * 1000) / ratePerThousand / 1000) * 1000;
      }
      return 0;
    }, [spendingCap]);

    // Validation — upper bound optional so enterprise / custom plans aren't blocked.
    const isValidCap = spendingCap >= MIN_SPENDING_CAP_USD &&
      (maxSpendingCap === undefined || spendingCap <= maxSpendingCap);

    const hasEditChanges = disclosureOpen && paygEnabled && spendingCap !== backendSpendingCap;

    const paygError = pricingOptionsFetcher?.state === "idle"
      ? getErrorFromFetcherData(pricingOptionsFetcher)
      : undefined;

    const handleDisclosureToggle = () => {
      if (!disclosureOpen) {
        setSpendingCap(backendSpendingCap);
        setPaygSubOption("payg");
      }
      setDisclosureOpen(!disclosureOpen);
    };

    const reset = () => {
      setSpendingCap(paygEnabled ? backendSpendingCap : MIN_SPENDING_CAP_USD);
      setPaygSubOption("payg");
      setDisclosureOpen(false);
    };

    const submit = () => {
      if (!isValidCap) return;

      const formData = new FormData();
      formData.append("pricing-option-action", "pay-as-you-go");
      formData.append("pay-as-you-go", "true");
      formData.append("pay-as-you-go-cap", spendingCap.toString());
      formData.append("pay-as-you-go-credits-cap", creditsCap.toString());

      if (!paygEnabled) {
        formData.append("pay-as-you-go-option",
          paygSubOption === "payg" ? "pay-as-you-go" : "pay-as-you-go-and-contact-sales"
        );
      }

      pricingOptionsFetcher.submit(formData, {
        method: "POST",
        action: PricingOptionsDataRoutePath
      });
    };

    // Expose imperative methods to parent via ref
    useImperativeHandle(ref, () => ({
      submit,
      reset,
    }));

    // Notify parent of state changes for button enable/disable
    useEffect(() => {
      onStateChange(hasEditChanges, isValidCap);
    }, [hasEditChanges, isValidCap, onStateChange]);

    return (
      <Card size="LG" className="bg-primary-50">
        {/* Toggle header when PAYG is already enabled */}
        {paygEnabled && (
          <button
            onClick={handleDisclosureToggle}
            className="flex items-center justify-between w-full text-left"
          >
            <p className="text-sm text-gray-900">
              <span className="font-medium">Pay-as-you-go enabled</span>
              <span className="text-gray-600"> - Current spending cap: ${backendSpendingCap}</span>
            </p>
            {disclosureOpen
              ? <RiArrowUpSLine className="w-5 h-5 text-gray-500 flex-shrink-0" />
              : <RiArrowDownSLine className="w-5 h-5 text-gray-500 flex-shrink-0" />
            }
          </button>
        )}

        {/* Expanded controls — always shown when not yet enabled, or when disclosure is open */}
        {(!paygEnabled || disclosureOpen) && (
          <>
            <p className="text-sm text-gray-700">
              Get additional credits at <span className="font-semibold">${ratePerThousand} per {fmtNumber(1000)} credits</span>.
              Set a spending cap to control costs while preventing service interruption.
            </p>

            {/* PAYG sub-options — only shown when enabling for the first time */}
            {!paygEnabled && (
              <div className="flex flex-col gap-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="payg-sub-option"
                    checked={paygSubOption === "payg"}
                    onChange={() => setPaygSubOption("payg")}
                    className="mt-1 w-4 h-4 text-brandPrimary focus:ring-brandPrimary border-gray-400"
                  />
                  <div>
                    <div className="font-medium text-gray-900 text-sm">Enable additional use</div>
                    <div className="text-xs text-gray-600">Get credits at ${ratePerThousand} per {fmtNumber(1000)} credits</div>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="payg-sub-option"
                    checked={paygSubOption === "payg-and-sales"}
                    onChange={() => setPaygSubOption("payg-and-sales")}
                    className="mt-1 w-4 h-4 text-brandPrimary focus:ring-brandPrimary border-gray-400"
                  />
                  <div>
                    <div className="font-medium text-gray-900 text-sm">Enable additional use and contact sales</div>
                    <div className="text-xs text-gray-600">Same as above, plus we'll connect you with sales for tailored guidance</div>
                  </div>
                </label>
              </div>
            )}

            {/* Spending cap input */}
            <div className="flex flex-col gap-2">
              <label className="text-sm text-gray-900">Spending cap (USD)</label>
              <div className="relative max-w-[200px]">
                <RiMoneyDollarCircleFill className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
                <input
                  type="number"
                  min={MIN_SPENDING_CAP_USD}
                  max={maxSpendingCap}
                  value={spendingCap}
                  onChange={(e) => setSpendingCap(e.target.valueAsNumber)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-brandPrimary focus:border-transparent"
                />
              </div>
              <p className="text-xs text-gray-600">
                Min ${MIN_SPENDING_CAP_USD}{maxSpendingCap ? `, max $${maxSpendingCap}` : ""}. We'll stop charging once you reach this limit.
              </p>
            </div>

            {/* PAYG error */}
            {paygError && (
              <WarningBox
                variant="danger"
                title="Failed to enable pay-as-you-go"
                items={[{ text: paygError.message || "Something went wrong. Please try again." }]}
              />
            )}
          </>
        )}
      </Card>
    );
  }
);

PayAsYouGoInlineControls.displayName = "PayAsYouGoInlineControls";

export default PayAsYouGoInlineControls;
